/**
 * AINA mentor `examPreference` helpers: target exam slug from enrolled program
 * plus latest diagnostic `exam_id` from get-latest-exam-details.
 */

import { AINA_AGENT_MENTOR } from "./ainaSessionUtils";

export type AinaProgramLike = {
  program_id?: string;
  mdsExamName?: string;
  examName?: string;
  program_name?: string;
};

/**
 * Map `program_id` (One App enrolled program) → stable target exam slug for AINA.
 * Extend when product adds programs; unknown ids fall back to exam/program labels.
 */
const PROGRAM_ID_TO_TARGET_EXAM: Record<string, string> = {};

/**
 * MDS/catalog `exam_id` → middle segment for mentor `examPreference`.
 * Final shape: `${target}${slug}_${examId}` when mapped (`examId` = raw API id, usually UUID);
 * unmapped catalog / id-only → `${target}_${examId}`.
 */
const LATEST_EXAM_ID_TO_EXAM_SLUG: Record<string, string> = {
  "1017": "foundation test",
  "3001": "cbse",
  "1003": "neet",
  "1001": "jee main",
  "1002": "jee advanced",
};

/** Compact / legacy program labels (spaces stripped) → display string for AINA `examPreference`. */
const COMPACT_EXAM_LABEL_TO_DISPLAY: Record<string, string> = {
  jeemain: "JEE Main",
  jeeadvanced: "JEE Advanced",
  foundationtest: "foundation test",
};

function expandCompactExamLabel(label: string): string {
  const key = label.replace(/\s+/g, "").toLowerCase();
  return COMPACT_EXAM_LABEL_TO_DISPLAY[key] ?? label;
}

export function getAinaTargetExamName(
  program: AinaProgramLike | null | undefined,
): string {
  if (!program) return "";
  const mapped =
    program.program_id != null
      ? PROGRAM_ID_TO_TARGET_EXAM[program.program_id]
      : undefined;
  if (mapped) return mapped;
  const label =
    program.mdsExamName ?? program.examName ?? program.program_name ?? "";
  const normalized = label
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .trim();
  if (!normalized) return "EXAM";
  return expandCompactExamLabel(normalized);
}

function readStringish(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return null;
}

function isNumericCatalogExamId(value: string): boolean {
  return /^\d+$/.test(value.trim());
}

/**
 * Prefer a numeric catalog exam id in `data` for slug lookup when root `exam_id` is a UUID.
 */
function pickCatalogExamIdFromLatestExamData(res: unknown): string | null {
  if (res == null || typeof res !== "object") return null;
  const r = res as Record<string, unknown>;
  const data = r.data;
  if (data != null && typeof data === "object" && !Array.isArray(data)) {
    const d = data as Record<string, unknown>;
    const preferredKeys = [
      "mds_exam_id",
      "mdsExamId",
      "exam_catalog_id",
      "catalog_exam_id",
      "exam_id",
      "examId",
    ] as const;
    for (const key of preferredKeys) {
      const s = readStringish(d[key]);
      if (s && isNumericCatalogExamId(s)) return s.trim();
    }
  }
  const fromRoot = readStringish(r.exam_id);
  if (fromRoot && isNumericCatalogExamId(fromRoot)) return fromRoot.trim();
  return null;
}

function resolveCatalogExamIdForAinaSlug(
  latestExamId: string | null | undefined,
  latestExamDetailsRes: unknown,
): string | null {
  const trimmed = latestExamId?.trim();
  if (trimmed && isNumericCatalogExamId(trimmed)) return trimmed;
  return pickCatalogExamIdFromLatestExamData(latestExamDetailsRes);
}

/**
 * Any non-empty latest `exam_id` from get-latest-exam-details (may be a UUID).
 * Used for **routing / gates** (`hasLatestExam` → mentor vs voice).
 */
export function extractLatestAinaExamId(res: unknown): string | null {
  if (res == null || typeof res !== "object") return null;
  const r = res as Record<string, unknown>;
  const fromRoot = readStringish(r.exam_id);
  if (fromRoot) return fromRoot;
  const data = r.data;
  if (data != null && typeof data === "object" && !Array.isArray(data)) {
    const d = data as Record<string, unknown>;
    const id = readStringish(d.exam_id);
    if (id) return id;
  }
  return null;
}

/**
 * Mentor: `${target}${slug}_${examId}` when catalog maps to `slug` and an `examId` is known;
 * `${target}_${examId}` when unmapped but `exam_id` / UUID exists; `${target}${slug}` if mapped but no id;
 * `target` if nothing resolves.
 */
export function buildAinaMentorExamPreference(
  program: AinaProgramLike | null | undefined,
  latestExamId: string | null | undefined,
  latestExamDetailsRes?: unknown,
): string {
  const target = getAinaTargetExamName(program);
  const catalogId = resolveCatalogExamIdForAinaSlug(
    latestExamId,
    latestExamDetailsRes ?? null,
  );
  const rawExamId = (
    latestExamId?.trim() ||
    extractLatestAinaExamId(latestExamDetailsRes ?? null) ||
    ""
  ).trim();
  const suffixId = (rawExamId || catalogId || "").trim();

  if (catalogId) {
    const slug = LATEST_EXAM_ID_TO_EXAM_SLUG[catalogId];
    if (slug && suffixId) {
      return `${target}${slug}_${suffixId}`;
    }
    if (slug) {
      return `${target}${slug}`;
    }
  }

  if (suffixId) {
    return `${target}_${suffixId}`;
  }

  return target;
}

export type AinaLoadUserSessionExamPreferenceOverrides = {
  examPreference?: string;
  agent?: string;
  latestExamId?: string | null;
  /** Raw get-latest-exam-details response; `exam_id` read when `latestExamId` is not set. */
  latestExamDetailsRes?: unknown;
};

/**
 * `user_info.exam_preference` for POST /session/load-user-session — same rules as host `examPreference`.
 */
export function resolveAinaExamPreferenceForLoadUserSession(
  program: AinaProgramLike | null | undefined,
  overrides?: AinaLoadUserSessionExamPreferenceOverrides,
): string {
  const agent = overrides?.agent?.trim();
  const isMentor =
    agent === AINA_AGENT_MENTOR ||
    (agent != null && agent.toUpperCase().includes("MENTOR"));

  const idFromOverride =
    overrides?.latestExamId == null
      ? ""
      : typeof overrides.latestExamId === "string"
        ? overrides.latestExamId.trim()
        : String(overrides.latestExamId).trim();
  const idFromRes = extractLatestAinaExamId(
    overrides?.latestExamDetailsRes ?? null,
  );
  const effectiveLatestId = (idFromOverride || idFromRes || "").trim() || "";

  if (
    isMentor &&
    (effectiveLatestId || overrides?.latestExamDetailsRes != null)
  ) {
    return buildAinaMentorExamPreference(
      program,
      effectiveLatestId || null,
      overrides?.latestExamDetailsRes,
    );
  }

  const explicit = overrides?.examPreference?.trim();
  if (explicit) return explicit;

  return getAinaTargetExamName(program) || "";
}
